import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@mui/material";
import { Eye, MoreVertical } from "lucide-react";
import { Menu, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../../hooks";
import { setCaseDetailModal } from "../../../../states/slices/globalReducer";

const columnHelper = createColumnHelper<any>();

const ActionMenu = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const userRoute = location.pathname.split("/")[1];
  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="flex items-center text-gray-400 hover:text-gray-600 focus:outline-none">
          <MoreVertical className="w-5 h-5" />
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          className="absolute right-0 mt-2 w-48 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50"
        >
          <div className="px-1 py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => navigate(`/${userRoute}/case-folders/view`)}
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#00B074]" : "text-gray-900"
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium`}
                >
                  Open folder
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => dispatch(setCaseDetailModal(true))}
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#00B074]" : "text-gray-900"
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium`}
                >
                  View case details
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  className={`${
                    active ? "bg-[#E6F7F1] text-[#00B074]" : "text-gray-900"
                  } group flex w-full items-center rounded-md px-2 py-2 text-sm font-medium`}
                >
                  Submit case folder
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export const CaseFolderColumns = [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("date", {
    header: "Date",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("title", {
    header: "Title",
    cell: (info) => (
      <span
        className="text-[#464255] font-medium max-w-[200px] truncate block"
        title={info.getValue()}
      >
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const status = info.getValue();
      let color = "#464255";
      if (status === "Awaiting Submission")
        color = "#D97706"; // Orange/Brownish
      else if (status === "Submitted") color = "#00B074";

      return (
        <span style={{ color }} className="font-medium">
          {status}
        </span>
      );
    },
  }),
  columnHelper.accessor("details", {
    header: "Details",
    cell: () => (
      <div className="flex items-center gap-x-2 text-[#464255] cursor-pointer hover:text-[#00B074]">
        <span>View</span>
        <Eye className="w-4 h-4" />
      </div>
    ),
  }),
  columnHelper.accessor("query_status", {
    header: "Query status",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("payment", {
    header: "Payment",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("appeal_number", {
    header: "Appeal number",
    cell: (info) => <span className="text-[#464255]">{info.getValue()}</span>,
  }),
  columnHelper.accessor("action", {
    header: "Action",
    cell: () => <ActionMenu />,
  }),
];
